import type { AlgoliaResultType, SearchType } from './Algolia';
import type LatLng from './LatLng';

type ResultBase = {
  id: string;
  containerClassName?: string;
};

export type StreetAddressSearchResult = {
  geo: LatLng;
  locationLabel?: string;
  name: string;
  type: 'address';
};

// There may be other fields on these types that just were not needed at the time, so were left out to keep
// the types less bloated. The server definitions can be found in the monolith at search/records/schema/*_record.json.
// If you think that algolia isn't returning a field that it should be, make sure that field is included in defaultAttributes in AlgoliaIndex.ts.
export type Coordinates = {
  lat: number;
  lng: number;
};

export type LocationType = 'area' | 'city' | 'country' | 'poi' | 'state' | 'photospot';

type RankingInfo = {
  geoDistance: number;
  geoPrecision: number;
  geoFractionalDigits: number;
  geoRanking: number;
  geoRankingScore: number;
};

export type AlgoliaHitBase = {
  _geoloc?: Coordinates;
  _cluster_geoloc?: Coordinates;
  _rankingInfo?: RankingInfo;
  ID: number;
  name: string;
  objectID: string; // area-1, etc
  slug: string;
  adjustedPopularity?: number;
  popularity?: number;
};

type AlgoliaHitLocationInfo = {
  city_id?: number;
  city_name?: string;
  country_id?: number;
  country_name?: string;
  country_name_source?: string;
  state_id?: number;
  state_name?: string;
  state_name_source?: string;
  type_label?: string;
  location_label?: string;
};

export type AreaType =
  | 'B' // BLM park
  | 'C' // Local park
  | 'O' // County park
  | 'F' // Forest park
  | 'H' // Historical park
  | 'I' // Historical site
  | 'M' // National monument
  | 'N' // National park
  | 'P' // National preserve park
  | 'A' // National recreational area park
  | 'E' // National seashore park
  | 'R' // Regional park
  | 'T' // State forest park
  | 'V' // State nature reserve park
  | 'S' // State park
  | 'W'; // Wilderness park

export type GuideMetadata = AlgoliaHitBase & {
  background_color?: string;
  name_with_formatting?: string;
  photo_url?: string;
  trail_count?: number;
};

type AlgoliaSearchResultBase = ResultBase & {
  searchType?: SearchType;
};

export type AreaHit = AlgoliaHitBase &
  AlgoliaHitLocationInfo & {
    area_type?: AreaType;
    area_type_string?: string;
    guides?: GuideMetadata[];
    has_guides?: boolean;
    location_display_name?: string;
    location_type?: LocationType;
    short_name?: string;
    popularity: number;
    type: 'area';
    trails_count?: number;
  };

export type AreaSearchResult = AlgoliaSearchResultBase & AreaHit;

export type FilterHit = AlgoliaHitBase & {
  filters?: {
    activity?: 'hiking'[];
    feature?: 'waterfall'[];
    suitability?: 'dogs'[];
  };
  type: 'filter';
};

export type FilterSearchResult = AlgoliaSearchResultBase & FilterHit;

export type GuideHit = AlgoliaHitBase &
  AlgoliaHitLocationInfo & {
    area_id?: number;
    area_name?: string;
    background_color?: string;
    name_with_formatting?: string;
    photo_url?: string;
    sub_topics?: ('hiking' | 'backpacking' | 'camping' | 'road-biking' | 'scenic-driving' | 'kids')[];
    topic?: 'top_picks' | 'hidden_gems' | 'activities';
    trail_count?: number;
    type: 'guide';
  };

export type GuideSearchResult = AlgoliaSearchResultBase & GuideHit;

export type ListHit = AlgoliaHitBase & { type: 'list' };

export type ListSearchResult = AlgoliaSearchResultBase & ListHit;

export type MapHit = AlgoliaHitBase &
  AlgoliaHitLocationInfo & {
    duration_minutes?: number;
    elevation_gain?: number;
    length?: number;
    photo_count?: number;
    private?: boolean;
    rating?: number;
    trail_id?: number;
    type: 'map';
    user?: {
      id: number;
      first_name?: string;
      last_name?: string;
      slug: string;
    };
  };

export type MapSearchResult = AlgoliaSearchResultBase & MapHit;

export type PageHit = AlgoliaHitBase & {
  image_url?: string;
  subtype?: 'guides';
  type: 'page';
};

export type PageSearchResult = AlgoliaSearchResultBase & PageHit;

export const poiSubTypes = [
  'alpine_hut',
  'archaeological_site',
  'attraction',
  'bay',
  'beach',
  'beach_hut',
  'bird_hide',
  'building',
  'campsite',
  'canal',
  'cape',
  'caravan_site',
  'castle',
  'cave',
  'church',
  'cliff',
  'coastline',
  'dam',
  'dune',
  'fort',
  'garden',
  'geyser',
  'glacier',
  'gorge',
  'historic_site',
  'hot_spring',
  'hut',
  'lagoon',
  'lake',
  'lock',
  'manor',
  'monastery',
  'monument',
  'peak',
  'picnic_site',
  'pond',
  'rapids',
  'resevoir',
  'river',
  'rock_shelter',
  'ruins',
  'spring',
  'stream',
  'sun_shelter',
  'tower',
  'valley',
  'viewpoint',
  'volcano',
  'waterfall',
  'weather_shelter',
  'wilderness_hut',
  'wildlife_hide'
] as const;
export type POISubType = (typeof poiSubTypes)[number];

export type POIHit = AlgoliaHitBase &
  AlgoliaHitLocationInfo & {
    area_name?: string;
    associated_area_ids?: number[];
    boundary_path?: string;
    collections?: string[];
    created_at?: number;
    elevation_meters?: number;
    indexed_at?: number;
    location_type?: LocationType;
    photos_count?: number;
    source?: string;
    subtype: POISubType;
    trails_count?: number;
    trail_ids?: number[];
    type: 'poi';
  };

export type POISearchResult = AlgoliaSearchResultBase & POIHit;
export type ClusteredPOIResult = POISearchResult & { clusterCoordinates?: Coordinates };

export type PhotospotHit = AlgoliaHitBase &
  AlgoliaHitLocationInfo & {
    type: 'photospot';
    photos_count: number;
    area_name: string;
    area_slug: string;
    location_type?: LocationType;
    subtype: 'photospot';
    trails_count?: number;
  };

export type PhotospotSearchResult = AlgoliaSearchResultBase & PhotospotHit;

export type SponsoredListHit = AlgoliaHitBase & {
  location_label: string;
  name_with_formatting: string;
  photo_url: string;
  sponsor_image_url: string;
  sponsor_name: string;
  type: 'sponsored_list';
  type_label: string;
};

export type SponsoredListSearchResult = AlgoliaSearchResultBase & SponsoredListHit;

// This encompasses the city, state, and country results, but both use the 'place' area type
export type PlaceHit = AlgoliaHitBase & AlgoliaHitLocationInfo & { curated_lists?: SponsoredListHit[]; location_type?: LocationType; type: 'place' };

export type PlaceSearchResult = AlgoliaSearchResultBase & PlaceHit;

export type TrackHit = Omit<MapHit, 'type'> & { type: 'track' };

export type TrackSearchResult = AlgoliaSearchResultBase & TrackHit;

export type TrailHit = AlgoliaHitBase &
  AlgoliaHitLocationInfo & {
    area_name?: string;
    area_id?: number;
    area_slug?: string;
    area_type?: AreaType;
    avg_rating?: number;
    city_url?: string;
    description?: string;
    difficulty_rating?: string;
    duration_minutes?: number;
    duration_minutes_cycling?: number;
    duration_minutes_hiking?: number;
    duration_minutes_mountain_biking?: number;
    duration_minutes_trail_running?: number;
    elevation_gain?: number;
    estimated_time_to_complete?: {
      value: string;
      unit: string;
    };
    estimated_time_to_complete_text?: string;
    _geoloc?: Coordinates;
    has_profile_photo?: boolean;
    highest_point?: number;
    is_closed?: boolean;
    length?: number;
    length_text?: string;
    num_photos?: number;
    num_reviews?: number;
    popularity?: number;
    profile_photo_url?: string;
    profile_photo_data?: string;
    polyline?: string;
    route_type?: 'O' | 'L' | 'P' | 'M';
    type: 'trail';
  };

export type TrailSearchResult = AlgoliaSearchResultBase & TrailHit;

export type CoordinatesResult = LatLng & {
  id: string;
  type: 'coordinates';
};

export type AlgoliaHit = AreaHit | FilterHit | GuideHit | ListHit | MapHit | PageHit | PlaceHit | POIHit | SponsoredListHit | TrackHit | TrailHit;

export type AlgoliaSearchResult =
  | AreaSearchResult
  | FilterSearchResult
  | GuideSearchResult
  | ListSearchResult
  | MapSearchResult
  | PageSearchResult
  | PlaceSearchResult
  | POISearchResult
  | SponsoredListSearchResult
  | TrackSearchResult
  | TrailSearchResult;

export type RecentAlgoliaHit = { type: AlgoliaResultType; objectID: string };

export type NearbyResult = { id: string; type: 'nearby'; searchType: 'nearby' };
export type TripPlannerResult = ResultBase & { type: 'trip_planner' }; // a fixed client-side result

export type NaturalLanguageSearchHit = { name: string; name_with_formatting: string; type: 'nls_query' };
export type NaturalLanguageSearchResult = ResultBase & NaturalLanguageSearchHit;

export type GuidesResult = AlgoliaSearchResultBase & { type: 'guides'; guides: GuideMetadata[]; areaName: string };

export type SponsoredListsResult = AlgoliaSearchResultBase & { type: 'sponsored-lists'; lists: SponsoredListHit[] };

export type StreetAddressResult = {
  id: string; // This doesn't come back in the request but we add it on later to meet the type expectations of our search components
} & StreetAddressSearchResult;
